import { useState, type ReactNode } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { IconSun, IconMoon } from '@tabler/icons-react';
import { HeaderTool, Page, PageHeader } from '@hana/shell';
import { Button, Segmented, StateView, useToast, type ScreenState } from '@hana/ui';
import { PAD_BACKUP, PAD_CH, PAD_DELEG0, PAD_KEYS, PAD_NOTI, PAD_SECS, PAD_SESSIONS, padNotiOn, type PadSec } from './settingsCompactData';
import { useT } from '@hana/i18n';
import './i18n';
import { USER } from './settings';
import s from './settingsCompact.module.css';

// Mint-Settings-iPad (personal): danh sách 6 mục + chi tiết. Dữ liệu mẫu; nút ghi chỉ đổi trạng thái trên máy + toast (chưa ghi DB).
// Tab "Quản trị" chuyển sang màn Admin (/kit).

function readState(): ScreenState | null {
  const v = new URLSearchParams(location.search).get('state');
  return v === 'loading' || v === 'empty' || v === 'error' || v === 'noperm' ? v : null;
}

const CHIP: Record<string, string | undefined> = { pri: s.pri, warn: s.warnC, ok: s.ok, mute: s.mute };
type Chip = readonly [string, 'pri' | 'warn' | 'ok' | 'mute'];

function Row({ k, sub, subWarn, r, rTone, chips, chev, onTap, children }: { k: string; sub?: string; subWarn?: boolean; r?: string; rTone?: 'link' | 'ok'; chips?: readonly Chip[]; chev?: boolean; onTap?: () => void; children?: ReactNode }) {
  const inner = (
    <>
      <div className={s.rl}>
        <div className={s.rk}>{k}</div>
        {sub && <div className={`${s.rs} ${subWarn ? s.rsW : ''}`}>{sub}</div>}
      </div>
      {chips && <div className={s.chips}>{chips.map(([v, t]) => <span key={v} className={`${s.chip} ${CHIP[t]}`}>{v}</span>)}</div>}
      {r && <span className={`${s.rr} ${rTone === 'link' ? s.rLink : rTone === 'ok' ? s.rOk : ''}`}>{r}</span>}
      {children}
      {chev && <span className={s.chev} aria-hidden="true">›</span>}
    </>
  );
  return onTap ? <button type="button" className={`${s.row} ${s.tap}`} onClick={onTap}>{inner}</button> : <div className={s.row}>{inner}</div>;
}
function Block({ h, children }: { h?: string; children: ReactNode }) {
  return <div>{h && <div className={s.bH}><b>{h}</b></div>}<div className={s.blk}>{children}</div></div>;
}
function Sw({ on, label, onChange }: { on: boolean; label: string; onChange: () => void }) {
  return <button type="button" role="switch" aria-checked={on} aria-label={label} className={`${s.sw} ${on ? s.swOn : ''}`} onClick={onChange}><span /></button>;
}
function Seg<K extends string>({ label, value, items, onChange }: { label: string; value: K; items: readonly (readonly [K, string])[]; onChange: (k: K) => void }) {
  return <div className={s.seg} role="radiogroup" aria-label={label}>{items.map(([k, l]) => <button key={k} type="button" role="radio" aria-checked={k === value} className={s.segB} onClick={() => onChange(k)}>{l}</button>)}</div>;
}

export function SettingsCompact() {
  const t = useT();
  const toast = useToast();
  const navigate = useNavigate();
  const forced = readState();
  const state: ScreenState = forced ?? 'ready';
  const [cur, setCur] = useState<PadSec>('profile');
  const [theme, setTheme] = useState('light');
  const [dense, setDense] = useState('std');
  const [lang, setLang] = useState('vi');
  const [sw, setSw] = useState<Record<string, boolean>>({ rm: false, bub: true, dnd: false });
  const [nOv, setNOv] = useState<Record<string, boolean>>({});
  const [dels, setDels] = useState<readonly (readonly [string, string])[]>(state === 'empty' ? [] : PAD_DELEG0);
  const [gone, setGone] = useState<string[]>([]);
  const say = (msg: string) => toast({ msg });
  const tog = (k: string, label: string) => () => { setSw((o) => ({ ...o, [k]: !o[k] })); say(`${sw[k] ? 'Đã tắt' : 'Đã bật'} ${label}`); };
  const sec = PAD_SECS.find((x) => x[0] === cur)!;
  const sessions = PAD_SESSIONS.filter((x) => !gone.includes(x[0]));

  const profile = (
    <>
      <div className={`${s.note}`}>{t('sys.cSelfAppr')}</div>
      <Block h="Tài khoản">
        <Row k="Nguyễn Trường Giang · DAVID" sub="gi•••@hanalogisticsvn.com · Microsoft 365" />
        <Row k="Đăng nhập" sub="Microsoft 365 · MFA bật" r="Đã xác minh" rTone="ok" />
      </Block>
      <Block h="Ghế đang giữ">
        <Row k="SALE-MGR · Trưởng phòng Kinh doanh" sub="từ 26/Jul/2026" chips={[['Chính', 'pri']]} />
        <Row k="BOD-DDIR · Phó Giám đốc" sub="từ 01/Oct/2026 · tự duyệt cấp BOD có cờ" chips={[['Quyền (Acting)', 'warn']]} />
        <Row k="ADMIN · Quản trị hệ thống" sub="cờ kỹ thuật, không theo ghế" chips={[['Cờ kỹ thuật', 'mute']]} />
      </Block>
    </>
  );
  const look = (
    <>
      <Block>
        <Row k="Chế độ màu" sub="Sáng hoặc tối"><Seg label="Chế độ màu" value={theme} onChange={setTheme} items={[['light', 'Sáng'], ['dark', 'Tối']]} /></Row>
        <Row k="Mật độ" sub="Chuẩn: dòng 48 · Gọn: dòng 40 (máy tính)"><Seg label="Mật độ" value={dense} onChange={setDense} items={[['std', 'Chuẩn'], ['dense', 'Gọn']]} /></Row>
        <Row k="Ngôn ngữ" sub="Mã nghiệp vụ (BL, ETD, VGM) giữ tiếng Anh"><Seg label="Ngôn ngữ" value={lang} onChange={setLang} items={[['vi', 'Tiếng Việt'], ['en', 'English']]} /></Row>
        <Row k="Giảm chuyển động" sub="Tắt hiệu ứng biểu đồ và icon nảy"><Sw on={sw.rm!} label="Giảm chuyển động" onChange={tog('rm', 'giảm chuyển động')} /></Row>
      </Block>
      <Block h="Bong bóng Hei">
        <Row k="Hiện bong bóng"><Sw on={sw.bub!} label="Hiện bong bóng Hei" onChange={tog('bub', 'bong bóng Hei')} /></Row>
        <Row k="Không làm phiền"><Sw on={sw.dnd!} label="Không làm phiền" onChange={tog('dnd', 'không làm phiền')} /></Row>
        <Row k="Vị trí" sub="Cạnh cú" />
      </Block>
    </>
  );
  const noti = (
    <>
      <div className={`${s.note} ${s.noteWarn}`}>{t('sys.cP0')}</div>
      <Block>
        {PAD_NOTI.map((n) => (
          <Row key={n.id} k={n.k} sub={n.s} subWarn={n.locked}>
            <div className={s.chs}>
              {PAD_CH.map((c, ci) => {
                const on = padNotiOn(n, ci, nOv);
                const lk = !!n.locked && n.def[ci] === 1;
                return <button key={c} type="button" role="switch" aria-checked={on} aria-label={`${n.k} qua ${c}`} className={s.ch} onClick={() => { if (lk) { say('Khẩn P0 không tắt được trong app'); return; } setNOv((o) => ({ ...o, [n.id + ci]: !on })); say(`${on ? 'Đã tắt' : 'Đã bật'} ${c}`); }}>{c}</button>;
              })}
            </div>
          </Row>
        ))}
      </Block>
    </>
  );
  const backup = (
    <>
      <div className={s.note}>{t('sys.cAbsent')}</div>
      <Block h="Backup của tôi">
        {PAD_BACKUP.map(([k, sub, who]) => <Row key={k} k={k} sub={sub} r={who} rTone="link" chev onTap={() => say(`Chọn backup ${k}: cùng phòng, bỏ người đang nghỉ`)} />)}
      </Block>
      <Block h="Uỷ quyền đang hiệu lực">
        {dels.length === 0
          ? <Row k="Chưa có uỷ quyền nào" sub="Khi anh nghỉ phép, việc tự chuyển theo backup" />
          : dels.map(([k, sub]) => <Row key={k} k={k} sub={sub}><button type="button" className={s.btn} onClick={() => { setDels((d) => d.filter((x) => x[0] !== k)); say('Đã kết thúc uỷ quyền'); }}>{t('sys.end')}</button></Row>)}
      </Block>
    </>
  );
  const sess = (
    <>
      <div className={`${s.note} ${s.noteWarn}`}>{t('sys.cClockOut')}</div>
      <Block>
        {sessions.map(([k, sub, me]) => (
          <Row key={k} k={k} sub={sub} chips={me ? [['Máy này', 'ok']] : undefined}>
            {!me && <button type="button" className={s.btn} onClick={() => { setGone((g) => [...g, k]); say(`Đã đăng xuất ${k}`); }}>{t('sys.signOut')}</button>}
          </Row>
        ))}
      </Block>
    </>
  );
  const keys = <Block>{PAD_KEYS.map(([k, v]) => <Row key={k} k={k} r={v} />)}</Block>;
  const BODY: Record<PadSec, ReactNode> = { profile, look, noti, backup, sessions: sess, keys };
  const DESC: Record<PadSec, string> = {
    profile: 'Ảnh đồng bộ Outlook / Teams · ghế do HR cập nhật theo quyết định', look: 'Áp mọi màn và mọi máy của anh', noti: '',
    backup: 'Backup theo ghế dùng khi nghỉ / báo vắng · uỷ quyền là tra cứu có hạn', sessions: 'Đăng xuất từ xa máy bị mất hoặc lạ', keys: 'Dùng khi gắn bàn phím vào iPad',
  };
  const TITLE: Record<PadSec, string> = { profile: 'Hồ sơ & ghế', look: 'Giao diện & ngôn ngữ', noti: 'Thông báo theo kênh', backup: 'Backup & uỷ quyền', sessions: 'Phiên đăng nhập & thiết bị', keys: 'Phím tắt bàn phím' };

  return (
    <Page>
      <PageHeader
        title="Cài đặt"
        tabs={<Segmented label="Phạm vi" value="personal" onChange={(k) => { if (k === 'admin') void navigate({ to: '/kit' }); }} items={[{ key: 'personal', label: 'Cá nhân' }, { key: 'admin', label: 'Quản trị' }]} />}
        actions={<HeaderTool label="Đổi giao diện sáng / tối" icon={theme === 'dark' ? IconSun : IconMoon} onClick={() => setTheme((t) => (t === 'dark' ? 'light' : 'dark'))} />}
      />
      <div className={s.wrap}>
        <nav className={s.nav} aria-label="Mục cài đặt">
          <div className={s.me}>
            <span className={s.av} aria-hidden="true">DV</span>
            <div className={s.meT}><div className={s.meK}>{USER.code} · {USER.name}</div><div className={s.meS}>{['SALE-MGR', 'BOD-DDIR (quyền)', 'ADMIN'].join(' · ')}</div></div>
          </div>
          <div className={s.items}>
            {PAD_SECS.map(([id, k, n]) => (
              <button key={id} type="button" className={s.item} aria-current={cur === id ? 'true' : undefined} onClick={() => setCur(id)}>
                <span className={s.itemK}>{k}</span>{n && <span className={s.itemN}>{n}</span>}
              </button>
            ))}
          </div>
        </nav>
        <section className={s.det} aria-label={sec[1]}>
          <div className={s.dHead}>
            <div className={s.dT}><h2>{TITLE[cur]}</h2>{DESC[cur] && <div className={s.dD}>{DESC[cur]}</div>}</div>
            {cur === 'profile' && (
              <>
                <a className={s.hBtn} href="/mee">{t('sys.openMee')}</a>
                <button type="button" className={s.hBtn} onClick={() => say('Ảnh mới sẽ đồng bộ lên Outlook / Teams')}>{t('sys.changePhoto')}</button>
              </>
            )}
            {cur === 'backup' && <button type="button" className={s.hBtn} onClick={() => { setDels((d) => [['Tất cả ghế → backup theo ghế', 'Hôm nay 23/Oct/2026 · bổ sung đơn nghỉ trong 2 ngày làm việc'] as const, ...d]); say('Đã báo vắng · việc chuyển cho backup'); }}>{t('sys.absentToday')}</button>}
            {cur === 'sessions' && <button type="button" className={s.hBtn} onClick={() => { setGone(PAD_SESSIONS.filter((x) => !x[2]).map((x) => x[0])); say('Đã đăng xuất 3 máy khác'); }}>{t('sys.signOutAll')}</button>}
          </div>
          <StateView state={state === 'empty' || state === 'noperm' ? 'ready' : state} title={state === 'error' ? 'Không tải được cài đặt của anh' : undefined} action={state === 'error' ? <Button onClick={() => location.reload()}>{t('sys.retry')}</Button> : undefined} skeleton={<div aria-busy="true" className={s.body}>{[1, 2, 3, 4, 5, 6, 7].map((i) => <div key={i} className={s.sk}><i /><i /></div>)}</div>}>
            <div className={s.body}>{BODY[cur]}</div>
          </StateView>
        </section>
      </div>
    </Page>
  );
}
